import {
	type ComponentType,
	forwardRef,
	type ReactNode,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from "react";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { DynamicFormRequirementNotice } from "@/lib/dynamicForms/components/dynamic-form-requirement-notice";
import { DynamicFormHostProvider } from "@/lib/dynamicForms/contexts/dynamic-form-host-context";
import {
	DynamicFormProvider,
	useDynamicFormContext,
} from "@/lib/dynamicForms/contexts/DynamicFormContext";
import type { DynamicFormSchema } from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import {
	RendererProvider,
	useOptionalRendererContext,
} from "@/lib/dynamicForms/renderers/cleanRendererSystem";
import {
	applyZodIssuesToFormFieldMeta,
	formHasFieldMetaErrors,
} from "@/lib/dynamicForms/utils/apply-zod-issues-to-form-field-meta.util";
import { areRequiredDynamicFormFieldsSatisfied } from "@/lib/dynamicForms/utils/are-required-dynamic-form-fields-satisfied.util";
import { generateZodSchema } from "@/lib/dynamicForms/utils/generateZodSchema.util";
import {
	type DynamicFormEntityKind,
	type DynamicFormRequirementMode,
	enforcesRequiredFields,
} from "@/lib/dynamicForms/utils/requirement-mode.util";
import { LayoutRenderer } from "@/lib/layoutRenderer/components/layout-renderer";

export interface CreateUtilityStep2FormApi {
	/** Current DynamicForm values. */
	getValues: () => Record<string, unknown>;
	/**
	 * Validates via TanStack field submit validators + schema Zod shape.
	 * Surfaces field meta errors for inline invalid UI.
	 * @returns ok + data when valid; ok false when invalid.
	 */
	validate: () => Promise<
		{ ok: true; data: Record<string, unknown> } | { ok: false; error: unknown }
	>;
}

export interface CreateUtilityStep2DynamicFormProps {
	schema: DynamicFormSchema;
	organizationId: string;
	/**
	 * Fires when Zod schema validity changes (required fields filled, etc.).
	 * Used to disable Add until Step 2 is complete.
	 */
	onValidityChange?: (isValid: boolean) => void;
	requirementMode: DynamicFormRequirementMode;
	entityKind: DynamicFormEntityKind;
}

function ValidityBridge({
	schema,
	values,
	fieldStates,
	onValidityChange,
	requirementMode,
}: {
	schema: DynamicFormSchema;
	values: Record<string, unknown>;
	fieldStates: Record<string, { shouldShow?: boolean; isRequired?: boolean }>;
	onValidityChange: (isValid: boolean) => void;
	requirementMode: DynamicFormRequirementMode;
}) {
	const lastValidRef = useRef<boolean | null>(null);

	useEffect(() => {
		const isValid = enforcesRequiredFields(requirementMode)
			? areRequiredDynamicFormFieldsSatisfied(schema, values, fieldStates)
			: true;
		if (lastValidRef.current === isValid) return;
		lastValidRef.current = isValid;
		onValidityChange(isValid);
	}, [schema, values, fieldStates, onValidityChange, requirementMode]);

	return null;
}

/**
 * Reports whether visible required Step 2 fields are filled (enables Add).
 * Full Zod runs on submit via {@link CreateUtilityStep2FormApi.validate}.
 */
function Step2ValidityReporter({
	schema,
	onValidityChange,
	requirementMode,
}: {
	schema: DynamicFormSchema;
	onValidityChange?: (isValid: boolean) => void;
	requirementMode: DynamicFormRequirementMode;
}) {
	const ctx = useDynamicFormContext();
	const FormSubscribe = ctx?.form?.Subscribe as
		| ComponentType<{
				selector: (state: {
					values: Record<string, unknown>;
				}) => Record<string, unknown>;
				children: (values: Record<string, unknown>) => ReactNode;
		  }>
		| undefined;

	if (!FormSubscribe || !onValidityChange) return null;

	return (
		<FormSubscribe selector={(state) => state.values}>
			{(values) => (
				<ValidityBridge
					schema={schema}
					values={(values ?? {}) as Record<string, unknown>}
					fieldStates={ctx?.fieldStates ?? {}}
					onValidityChange={onValidityChange}
					requirementMode={requirementMode}
				/>
			)}
		</FormSubscribe>
	);
}

function CreateUtilityStep2FormInner({
	schema,
	forwardedRef,
	requirementMode,
	entityKind,
}: {
	schema: DynamicFormSchema;
	forwardedRef: React.ForwardedRef<CreateUtilityStep2FormApi>;
	requirementMode: DynamicFormRequirementMode;
	entityKind: DynamicFormEntityKind;
}) {
	const { t } = useAppTranslation();
	const ctx = useDynamicFormContext();

	useImperativeHandle(
		forwardedRef,
		() => ({
			getValues: () => {
				const values = ctx?.form?.state?.values;
				return (values ?? {}) as Record<string, unknown>;
			},
			validate: async () => {
				const form = ctx?.form;
				if (!form) {
					return {
						ok: false as const,
						error: new Error("Form is not ready"),
					};
				}

				await form.validateAllFields("submit");

				const values = (form.state?.values ?? {}) as Record<string, unknown>;
				const zodSchema = generateZodSchema(schema, t, {
					enforceRequired: enforcesRequiredFields(requirementMode),
				});
				const result = zodSchema.safeParse(values);

				if (!result.success) {
					applyZodIssuesToFormFieldMeta(form, result.error);
					return { ok: false as const, error: result.error };
				}

				if (formHasFieldMetaErrors(form)) {
					return {
						ok: false as const,
						error: new Error("Field validation failed"),
					};
				}

				return {
					ok: true as const,
					data: result.data as Record<string, unknown>,
				};
			},
		}),
		[ctx?.form, requirementMode, schema, t],
	);

	if (!schema.layoutJson) {
		return null;
	}

	return (
		<div className="flex w-full flex-col gap-(--spacing-3xl,24px)">
			<DynamicFormRequirementNotice
				mode={requirementMode}
				entityKind={entityKind}
				fieldDefinitions={schema.fieldDefinitions}
			/>
			<LayoutRenderer
				schema={schema}
				fieldDefinitions={schema.fieldDefinitions}
				node={schema.layoutJson}
			/>
		</div>
	);
}

/**
 * Entity-type DynamicForm for create utility modal Step 2 with validate/getValues API.
 */
export const CreateUtilityStep2DynamicForm = forwardRef<
	CreateUtilityStep2FormApi,
	CreateUtilityStep2DynamicFormProps
>(function CreateUtilityStep2DynamicForm(
	{ schema, organizationId, onValidityChange, requirementMode, entityKind },
	ref,
) {
	const parentContext = useOptionalRendererContext();

	const fieldRenderers = useMemo(
		() => parentContext?.fieldRenderers ?? {},
		[parentContext?.fieldRenderers],
	);
	const elementRenderers = useMemo(
		() => parentContext?.elementRenderers ?? {},
		[parentContext?.elementRenderers],
	);

	return (
		<RendererProvider
			fieldRenderers={fieldRenderers}
			elementRenderers={elementRenderers}
		>
			<DynamicFormHostProvider
				value={{
					organizationId: organizationId === "unknown" ? "" : organizationId,
					runtime: false,
				}}
			>
				<DynamicFormProvider
					schema={schema}
					previewMode
					requirementMode={requirementMode}
					onSubmit={() => {}}
				>
					<Step2ValidityReporter
						schema={schema}
						onValidityChange={onValidityChange}
						requirementMode={requirementMode}
					/>
					<CreateUtilityStep2FormInner
						schema={schema}
						forwardedRef={ref}
						requirementMode={requirementMode}
						entityKind={entityKind}
					/>
				</DynamicFormProvider>
			</DynamicFormHostProvider>
		</RendererProvider>
	);
});
